import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, BarChart3, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { useAdminAuth } from '../store/adminAuth';
import CyrenzaLogo from '../assets/cyrenza-logo.svg';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', label: 'Overview', icon: LayoutDashboard },
    { to: '/users', label: 'Users', icon: Users },
    { to: '/tenants', label: 'Tenants', icon: Building2 },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar - Matching workspace aesthetic */}
      <aside className="w-64 bg-card flex flex-col border-r border-border">
        {/* Logo */}
        <div className="h-14 flex items-center px-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-primary p-1 flex items-center justify-center border border-border">
              <img src={CyrenzaLogo} alt="Cyrenza" className="w-4 h-4 object-contain filter brightness-0 invert" />
            </div>
            <span className="text-body-small text-foreground">Admin</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-2 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2 py-1.5 text-body-small font-normal tracking-tight rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`
              }
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User info & Logout */}
        <div className="border-t border-border p-2">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-body-small min-w-0">
              <div className="font-normal tracking-tight text-foreground truncate">{user?.full_name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-body-small font-normal tracking-tight text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-background">
        <div className="w-full max-w-6xl mx-auto px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}
